import { useCallback, useMemo } from 'react';
import {
  useLazyQualityDetails,
  useQualityDetails,
  type QualityColumnDescriptor,
  type QualityDetailItem,
  type QualityDetailsParams,
} from '@/entities/dashboards';
import { useLazyGridPages } from '@/shared/hooks';

/**
 * Размер окна выдачи таблицы «Все детали». Строка таблицы несёт баллы по всем критериям выборки
 * и запрошенные атрибуты, поэтому окно заметно меньше серверного потолка.
 */
const ALL_DETAILS_PAGE_SIZE = 100;

/** Заглавное окно: из него нужны только описания колонок и одна строка-образец. */
const HEAD_QUERY_LIMIT = 1;

/** Условия отбора таблицы «Все детали» — всё, кроме самого окна выдачи. */
export type AllDetailsQuery = Omit<QualityDetailsParams, 'limit' | 'offset'>;

/** Постраничный набор таблицы «Все детали» и то, что нужно её колонкам. */
export type AllDetailsPages = {
  /** Колонки таблицы известны — до этого её показывать нечем */
  isReady: boolean;
  /** Число оценок всей выборки — им подписана таблица */
  totalCount: number;
  /** Размер окна выдачи */
  pageSize: number;
  /** Сигнатура отбора: её смена начинает набор заново */
  cacheVersion: string;
  /** Ключ пересоздания таблицы, пока набор пуст */
  gridKey: string;
  /** Загрузка одного окна выдачи */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<QualityDetailItem[]>;
  /** Описания доступных колонок (системная конфигурация) */
  availableColumns: QualityColumnDescriptor[];
  /** Строка-образец выборки: по ней имя критерия сопоставляется его идентификатором */
  sampleRow: QualityDetailItem | null;
};

/**
 * Таблица «Все детали» окнами выдачи: прокрутка доводит до последней оценки выборки, а её общее
 * число берётся из того же ответа. Отбор и порядок остаются за бэкендом — окно лишь называет,
 * какую часть уже отобранного показать.
 *
 * Заглавное окно в одну строку идёт до самих окон таблицы: из него берутся описания колонок и
 * строка-образец, которой имя критерия сопоставляется идентификатором для серверных сортировки
 * и фильтра по критерию. Того и другого таблице не хватает уже к первому показу — состав колонок
 * она запоминает именно тогда, и пришедший позже опоздал бы к настройкам пользователя.
 */
export const useAllDetailsPages = (query: AllDetailsQuery): AllDetailsPages => {
  // Состав атрибутных колонок заглавному окну не нужен: из строки-образца берутся только её
  // критерии. Без него включение и выключение атрибутной колонки заглавное окно не трогает.
  const headQuery = useMemo(() => ({ ...query, columns: undefined, limit: HEAD_QUERY_LIMIT }), [query]);

  const { data: headData } = useQualityDetails(headQuery);
  const availableColumns = useMemo(() => headData?.availableColumns ?? [], [headData?.availableColumns]);

  const { refetch } = useLazyQualityDetails();

  const cacheVersion = useMemo(() => JSON.stringify(query), [query]);

  const loadPage = useCallback(
    async (offset: number, limit: number) => {
      const data = await refetch({ ...query, offset, limit });
      return { items: data.items, total: data.total };
    },
    [refetch, query],
  );

  const { totalCount, gridKey, fetchPage } = useLazyGridPages<QualityDetailItem>({
    cacheVersion,
    fetchPage: loadPage,
  });

  return {
    isReady: headData !== undefined,
    totalCount,
    pageSize: ALL_DETAILS_PAGE_SIZE,
    cacheVersion,
    gridKey,
    fetchPage,
    availableColumns,
    sampleRow: headData?.items[0] ?? null,
  };
};
